import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  useTheme,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  useMediaQuery,
  Paper,
  Stack,
  ButtonGroup,
  Tooltip,
  Fade,
  alpha
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Group as GroupIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon,
  ReceiptLong as ReceiptLongIcon,
  Percent as PercentIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { fetchOrders } from '../ordersApi';
import { useBranch } from '../BranchContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

// ==================== CURRENCY UTILITIES ====================
const SUPPORTED_CURRENCIES = {
  YER: { symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0, color: '#1cc88a' },
  YRN: { symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0, color: '#e67e22' },
  USD: { symbol: '$', name: 'دولار أمريكي', decimals: 2, color: '#4e73df' },
  SAR: { symbol: 'ر.س', name: 'ريال سعودي', decimals: 2, color: '#36b9cc' }
};

const CONVERSION_RATES = {
  YER: { YER: 1, YRN: 2.7, USD: 0.004, SAR: 0.015 },
  YRN: { YER: 0.37, YRN: 1, USD: 0.0015, SAR: 0.0056 },
  USD: { YER: 250, YRN: 675, USD: 1, SAR: 3.75 },
  SAR: { YER: 66.67, YRN: 180, USD: 0.27, SAR: 1 }
};

// ✅ FIXED: Safe conversion with validation
const convertAmount = (amount, fromCurrency, toCurrency) => {
  // Convert to number and validate
  const numAmount = parseFloat(amount);
  
  // Handle invalid inputs
  if (isNaN(numAmount) || numAmount === null || numAmount === undefined) {
    return 0;
  }
  
  // Same currency, no conversion needed
  if (fromCurrency === toCurrency) return numAmount;
  
  // Get conversion rate
  const rate = CONVERSION_RATES[fromCurrency]?.[toCurrency];
  if (!rate) {
    console.warn(`No conversion rate from ${fromCurrency} to ${toCurrency}`);
    return numAmount;
  }
  
  return numAmount * rate;
};

// ✅ FIXED: Safe formatting with validation
const formatCurrency = (amount, currency) => {
  // Convert to number and validate
  const numAmount = parseFloat(amount);
  
  // Handle invalid inputs
  if (isNaN(numAmount) || numAmount === null || numAmount === undefined) {
    return '0';
  }
  
  // Get currency info
  const currencyInfo = SUPPORTED_CURRENCIES[currency];
  if (!currencyInfo) {
    return numAmount.toFixed(2);
  }
  
  return numAmount.toFixed(currencyInfo.decimals);
};

// ✅ Safe currency symbol getter
const getCurrencySymbol = (currency) => {
  return SUPPORTED_CURRENCIES[currency]?.symbol || '';
};

// ==================== COMPONENT ====================
const BranchSales = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedBranch } = useBranch();
  
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [latestOrders, setLatestOrders] = useState([]);
  const [salesBreakdown, setSalesBreakdown] = useState([]);
  const [breakdownType, setBreakdownType] = useState('daily');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // ✅ NEW: Display currency selector
  const [displayCurrency, setDisplayCurrency] = useState('YER');

  useEffect(() => {
    if (selectedBranch) {
      loadDashboardData();
    }
  }, [breakdownType, selectedBranch]);

  const loadDashboardData = async () => {
    if (!selectedBranch) return;

    setLoading(true);
    try {
      const allOrders = await fetchOrders(selectedBranch.id || selectedBranch.Id);
      
      const validOrders = allOrders.filter(o => {
        const status = (o.OrderStatus || o.orderStatus || '').toLowerCase();
        return ['served', 'delivered', 'completed', 'readytoserve'].includes(status);
      });

      // ✅ Calculate with currency conversion
      let totalSales = 0;
      let totalSubTotal = 0;
      let totalDeliveryFees = 0;
      
      validOrders.forEach(o => {
        const orderCurrency = o.Currency || o.currency || 'YER';
        totalSales += convertAmount(o.TotalAmount || o.totalAmount || 0, orderCurrency, 'YER');
        totalSubTotal += convertAmount(o.SubTotal || o.subTotal || 0, orderCurrency, 'YER');
        totalDeliveryFees += convertAmount(o.DeliveryFee || o.deliveryFee || 0, orderCurrency, 'YER');
      });

      const totalOrders = validOrders.length;
      
      // Calculate Products Sold & Top Products
      let productsMap = {};
      let totalProducts = 0;
      
      validOrders.forEach(o => {
        const itemsJson = o.OrderItemsJson || o.orderItemsJson;
        if (itemsJson) {
          try {
            const items = JSON.parse(itemsJson);
            if (Array.isArray(items)) {
              items.forEach(item => {
                const qty = item.Quantity || item.quantity || 1;
                totalProducts += qty;
                const name = item.ProductName || item.productName || 'Unknown';
                const price = item.Price || item.price || 0;
                const orderCurrency = o.Currency || o.currency || 'YER';
                const convertedPrice = convertAmount(price, orderCurrency, 'YER');
                
                if (!productsMap[name]) productsMap[name] = 0;
                productsMap[name] += (convertedPrice * qty);
              });
            }
          } catch (e) { console.error("Error parsing items", e); }
        }
      });

      const topProducts = Object.entries(productsMap)
        .map(([name, sales]) => ({ productName: name, totalSales: sales }))
        .sort((a, b) => b.totalSales - a.totalSales)
        .slice(0, 5);

      const uniqueUsers = new Set(validOrders.map(o => o.CustomerName || o.customerName)).size;

      setSummaryData({
        totalSales,
        totalSubTotal,
        totalDeliveryFees,
        totalOrders,
        totalProducts,
        totalUsers: uniqueUsers,
        topProducts
      });

      // Calculate Breakdown (in YER base)
      const breakdown = {};
      validOrders.forEach(o => {
        const rawDate = o.CreatedDate || o.createdDate || o.OrderDate || o.orderDate;
        if (!rawDate) return;
        
        const orderDate = new Date(rawDate);
        const adjustedDate = new Date(orderDate.getTime() + (11 * 60 * 60 * 1000));
        
        let key = '';
        if (breakdownType === 'daily') {
          key = adjustedDate.toLocaleDateString('en-CA');
        } else if (breakdownType === 'monthly') {
          key = `${adjustedDate.getMonth() + 1}/${adjustedDate.getFullYear()}`;
        } else {
          const firstDay = new Date(adjustedDate.getFullYear(), 0, 1);
          const pastDays = (adjustedDate - firstDay) / 86400000;
          const weekNum = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
          key = `Week ${weekNum}`;
        }

        if (!breakdown[key]) breakdown[key] = 0;
        const orderCurrency = o.Currency || o.currency || 'YER';
        breakdown[key] += convertAmount(o.TotalAmount || o.totalAmount || 0, orderCurrency, 'YER');
      });

      const breakdownData = Object.entries(breakdown).map(([key, val]) => ({
        date: key,
        week: key.replace('Week ', ''),
        month: key.split('/')[0],
        year: key.split('/')[1],
        totalSales: val
      })).sort((a, b) => {
        if (breakdownType === 'daily') return new Date(a.date) - new Date(b.date);
        return 0;
      });

      setSalesBreakdown(breakdownData);
      setLatestOrders(allOrders.sort((a, b) => new Date(b.CreatedDate || b.createdDate) - new Date(a.CreatedDate || a.createdDate)).slice(0, 10));

    } catch (error) {
      console.error("Error loading dashboard:", error);
      setSummaryData({ totalSales: 0, totalOrders: 0, totalProducts: 0, totalUsers: 0, topProducts: [], totalSubTotal: 0, totalDeliveryFees: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!latestOrders.length) return;
    
    const header = ['Order ID', 'Date', 'Customer', 'Currency', 'Amount', 'Status'];
    const rows = latestOrders.map(o => [
      o.OrderId || o.orderId,
      (o.CreatedDate || o.createdDate) ? new Date(o.CreatedDate || o.createdDate).toLocaleString() : '',
      o.CustomerName || o.customerName || '',
      o.Currency || o.currency || 'YER',
      `${formatCurrency(o.TotalAmount || o.totalAmount || 0, o.Currency || 'YER')} ${getCurrencySymbol(o.Currency || 'YER')}`,
      (o.OrderStatus || o.orderStatus) || 'Pending'
    ]);
    
    let csvContent = "\uFEFF" + header.join(',') + '\n' + 
      rows.map(r => r.map(x => `"${String(x).replace(/"/g,'""')}"`).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `branch_sales_${selectedBranch?.name || 'branch'}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getChartData = () => {
    if (!salesBreakdown || salesBreakdown.length === 0) return { labels: [], datasets: [] };

    let labels = [];
    let data = [];

    if (breakdownType === 'daily') {
      labels = salesBreakdown.map(d => new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
      data = salesBreakdown.map(d => convertAmount(d.totalSales, 'YER', displayCurrency));
    } else if (breakdownType === 'weekly') {
      labels = salesBreakdown.map(d => `Week ${d.week}`);
      data = salesBreakdown.map(d => convertAmount(d.totalSales, 'YER', displayCurrency));
    } else {
      labels = salesBreakdown.map(d => `${d.month}/${d.year}`);
      data = salesBreakdown.map(d => convertAmount(d.totalSales, 'YER', displayCurrency));
    }

    return {
      labels,
      datasets: [{
        label: `Sales (${displayCurrency})`,
        data: data,
        fill: true,
        backgroundColor: alpha(SUPPORTED_CURRENCIES[displayCurrency].color, 0.1),
        borderColor: SUPPORTED_CURRENCIES[displayCurrency].color,
        tension: 0.4,
        pointRadius: isMobile ? 2 : 3,
        pointBackgroundColor: SUPPORTED_CURRENCIES[displayCurrency].color,
      }],
    };
  };

  const getTopProductsData = () => {
    const products = summaryData?.topProducts;
    if (!summaryData || !products) return { labels: [], datasets: [] };
    
    return {
      labels: products.map(p => p.productName),
      datasets: [{
        data: products.map(p => convertAmount(p.totalSales, 'YER', displayCurrency)),
        backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'],
        hoverOffset: 4
      }]
    };
  };

  const KPICard = ({ title, value, icon, color, subtext }) => (
    <Fade in timeout={600}>
      <Card sx={{ 
        height: '100%', 
        borderLeft: `4px solid ${color}`, 
        boxShadow: 3,
        transition: 'all 0.3s',
        '&:hover': { 
          transform: isMobile ? 'none' : 'translateY(-4px)',
          boxShadow: 6
        }
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="subtitle2" 
                color={color} 
                fontWeight="bold" 
                gutterBottom 
                sx={{ 
                  textTransform: 'uppercase', 
                  letterSpacing: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="text.primary"
                sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
              >
                {value}
              </Typography>
            </Box>
            <Box sx={{ 
              color: alpha(color, 0.2),
              display: { xs: 'none', sm: 'block' }
            }}>
              {React.cloneElement(icon, { sx: { fontSize: { sm: 35, md: 40 } } })}
            </Box>
          </Box>
          {subtext && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mt: { xs: 1, sm: 2 }, 
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              {subtext}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  const handleOpenOrder = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleCloseOrder = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  };

  if (!selectedBranch) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('PleaseSelectBranch')}</Alert>
      </Box>
    );
  }

  if (loading && !summaryData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={isMobile ? 40 : 60} />
      </Box>
    );
  }

  const displayTotalSales = convertAmount(summaryData?.totalSales || 0, 'YER', displayCurrency);
  const displaySubTotal = convertAmount(summaryData?.totalSubTotal || 0, 'YER', displayCurrency);
  const displayDeliveryFees = convertAmount(summaryData?.totalDeliveryFees || 0, 'YER', displayCurrency);

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header with Currency Selector */}
      <Paper 
        elevation={2}
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 3, sm: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.light, 0.02)})`
        }}
      >
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={{ xs: 2, sm: 3 }}
        >
          <Box>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              color="text.primary" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
            >
              <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 'inherit' }} />
              {selectedBranch.name} {t('SalesDashboard') || 'Sales Dashboard'}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              {t('salesDashboardSubtitle') || 'Real-time sales analytics and insights'}
            </Typography>
          </Box>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            {/* ✅ Currency Selector */}
            <ButtonGroup 
              variant="contained"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {Object.entries(SUPPORTED_CURRENCIES).map(([code, info]) => (
                <Button
                  key={code}
                  onClick={() => setDisplayCurrency(code)}
                  sx={{
                    bgcolor: displayCurrency === code ? info.color : alpha(info.color, 0.2),
                    color: displayCurrency === code ? 'white' : info.color,
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 2, sm: 3 },
                    '&:hover': {
                      bgcolor: displayCurrency === code ? info.color : alpha(info.color, 0.3)
                    }
                  }}
                >
                  {info.symbol} {code}
                </Button>
              ))}
            </ButtonGroup>
            
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
              onClick={loadDashboardData}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
            >
              {t('refresh')}
            </Button>
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
              onClick={handleExport}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{ 
                bgcolor: '#4e73df',
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              }}
            >
              {isMobile ? t('export') : t('generateReport')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('totalSales')} 
            value={`${formatCurrency(displayTotalSales, displayCurrency)} ${getCurrencySymbol(displayCurrency)}`}
            icon={<AttachMoneyIcon />} 
            color="#4e73df"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('fivePercentSales') || '5% Commission'}
            value={`${formatCurrency(displayTotalSales * 0.05, displayCurrency)} ${getCurrencySymbol(displayCurrency)}`}
            icon={<PercentIcon />} 
            color="#1cc88a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('totalSubTotal')} 
            value={`${formatCurrency(displaySubTotal, displayCurrency)} ${getCurrencySymbol(displayCurrency)}`}
            icon={<ReceiptLongIcon />} 
            color="#36b9cc"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
             title={t('fivePercentSubTotal') || '5% of Subtotal'}
             value={`${formatCurrency(displaySubTotal * 0.05, displayCurrency)} ${getCurrencySymbol(displayCurrency)}`}
             icon={<PercentIcon />}
             color="#f6c23e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('totalDeliveryFees')} 
            value={`${formatCurrency(displayDeliveryFees, displayCurrency)} ${getCurrencySymbol(displayCurrency)}`}
            icon={<LocalShippingIcon />} 
            color="#e74a3b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('totalOrders')} 
            value={(summaryData?.totalOrders || 0).toLocaleString()} 
            icon={<ShoppingCartIcon />} 
            color="#1cc88a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('productsSold')} 
            value={(summaryData?.totalProducts || 0).toLocaleString()} 
            icon={<InventoryIcon />} 
            color="#e74a3b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            title={t('totalUsers')} 
            value={(summaryData?.totalUsers || 0).toLocaleString()} 
            icon={<GroupIcon />} 
            color="#858796"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        {/* Sales Overview Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%', boxShadow: 3, borderRadius: 3 }}>
            <Box sx={{ 
              p: { xs: 2, sm: 2.5 }, 
              borderBottom: '1px solid #e3e6f0', 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                color="primary"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                {t('salesOverview')}
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                {['daily', 'weekly', 'monthly'].map((type) => (
                  <Button
                    key={type}
                    variant={breakdownType === type ? 'contained' : 'outlined'}
                    onClick={() => setBreakdownType(type)}
                    sx={{ 
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      px: { xs: 1.5, sm: 2 }
                    }}
                  >
                    {t(type)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
            <CardContent sx={{ 
              height: { xs: 280, sm: 320, md: 380 },
              p: { xs: 1, sm: 2 }
            }}>
              <Line 
                data={getChartData()} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { 
                      display: !isMobile,
                      labels: {
                        font: { size: isMobile ? 10 : 12 }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      titleFont: { size: isMobile ? 11 : 13 },
                      bodyFont: { size: isMobile ? 10 : 12 }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { borderDash: [2] },
                      ticks: { 
                        font: { size: isMobile ? 9 : 11 },
                        callback: (value) => `${formatCurrency(value, displayCurrency)}`
                      }
                    },
                    x: {
                      grid: { display: false },
                      ticks: { 
                        font: { size: isMobile ? 9 : 11 },
                        maxRotation: isMobile ? 45 : 0
                      }
                    }
                  }
                }} 
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', boxShadow: 3, borderRadius: 3 }}>
            <Box sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #e3e6f0' }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                color="primary"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                {t('topProducts')}
              </Typography>
            </Box>
            <CardContent sx={{ 
              height: { xs: 280, sm: 320, md: 380 },
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: { xs: 1, sm: 2 }
            }}>
              <Doughnut 
                data={getTopProductsData()} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { 
                      position: 'bottom',
                      labels: {
                        font: { size: isMobile ? 9 : 11 },
                        padding: isMobile ? 8 : 12
                      }
                    },
                    tooltip: {
                      titleFont: { size: isMobile ? 11 : 13 },
                      bodyFont: { size: isMobile ? 10 : 12 }
                    }
                  },
                  cutout: '70%'
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Orders Table */}
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #e3e6f0' }}>
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            color="primary"
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            {t('recentOrders')}
          </Typography>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead sx={{ bgcolor: '#f8f9fc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('orderId')}</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{t('date')}</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('customer')}</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{t('currency')}</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('amount')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {latestOrders.length > 0 ? (
                latestOrders.map((order) => {
                  const orderCurrency = order.Currency || order.currency || 'YER';
                  const orderAmount = order.TotalAmount || order.totalAmount || 0;
                  const displayAmount = convertAmount(orderAmount, orderCurrency, displayCurrency);
                  
                  return (
                    <TableRow 
                      key={order.Id || order.id} 
                      hover
                      onClick={() => handleOpenOrder(order)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 'bold', color: '#4e73df', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        #{order.OrderNumber || order.orderNumber || order.OrderId || order.orderId}
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {(() => {
                            const d = new Date(order.CreatedDate || order.createdDate);
                            if (isNaN(d.getTime())) return t('invalidDate');
                            const adjusted = new Date(d.getTime() + (11 * 60 * 60 * 1000));
                            return adjusted.toLocaleDateString();
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {!isMobile && (
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#36b9cc', fontSize: 13, mr: 1 }}>
                              {(order.CustomerName || order.customerName || '?').charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                          <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {order.CustomerName || order.customerName || t('unknown')}
                          </Typography>
                        </Box>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip 
                            label={orderCurrency}
                            size="small"
                            sx={{ 
                              bgcolor: alpha(SUPPORTED_CURRENCIES[orderCurrency]?.color || '#4e73df', 0.1),
                              color: SUPPORTED_CURRENCIES[orderCurrency]?.color || '#4e73df',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {formatCurrency(displayAmount, displayCurrency)} {getCurrencySymbol(displayCurrency)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.OrderStatus || order.orderStatus || 'Pending'} 
                          size="small"
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            bgcolor: ['Served', 'Delivered', 'Completed'].includes(order.OrderStatus || order.orderStatus) ? '#e8f5e9' : '#fff3e0',
                            color: ['Served', 'Delivered', 'Completed'].includes(order.OrderStatus || order.orderStatus) ? '#2e7d32' : '#ef6c00'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={isMobile ? 4 : 6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('noRecentOrdersFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Order Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseOrder} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {t('OrderDetails')} #{selectedOrder?.orderId || selectedOrder?.OrderId}
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
             <Grid container spacing={{ xs: 2, sm: 3 }}>
               <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                   {t('customerDetails')}
                 </Typography>
                 <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                   <strong>{t('customer')}:</strong> {selectedOrder.customerName || selectedOrder.CustomerName || 'Guest'}
                 </Typography>
                 <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                   <strong>{t('currency')}:</strong> {selectedOrder.Currency || selectedOrder.currency || 'YER'}
                 </Typography>
               </Grid>
               
               <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                   {t('paymentSummary')}
                 </Typography>
                 <List dense>
                   {!(selectedOrder.IsPreOrderDineIn || selectedOrder.isPreOrderDineIn) && (
                     <>
                       <ListItem>
                         <ListItemText 
                           primary={t('subTotal')} 
                           primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                         />
                         <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                           {formatCurrency(selectedOrder.subTotal || selectedOrder.SubTotal || 0, selectedOrder.Currency || 'YER')} {getCurrencySymbol(selectedOrder.Currency || 'YER')}
                         </Typography>
                       </ListItem>
                       <ListItem>
                         <ListItemText 
                           primary={t('deliveryFee')}
                           primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                         />
                         <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                           {formatCurrency(selectedOrder.deliveryFee || selectedOrder.DeliveryFee || 0, selectedOrder.Currency || 'YER')} {getCurrencySymbol(selectedOrder.Currency || 'YER')}
                         </Typography>
                       </ListItem>
                     </>
                   )}
                   <ListItem>
                     <ListItemText 
                       primary={t('totalAmount')} 
                       primaryTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }} 
                     />
                     <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                       {formatCurrency(selectedOrder.totalAmount || selectedOrder.TotalAmount || 0, selectedOrder.Currency || 'YER')} {getCurrencySymbol(selectedOrder.Currency || 'YER')}
                     </Typography>
                   </ListItem>
                 </List>
               </Grid>

               <Grid item xs={12}>
                 <Divider sx={{ my: 2 }} />
                 <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                   {t('OrderItems')}
                 </Typography>
                 {(() => {
                   let items = [];
                   try {
                     const raw = selectedOrder.OrderItemsJson || selectedOrder.orderItemsJson;
                     if (raw) {
                       items = typeof raw === 'string' ? JSON.parse(raw) : raw;
                     }
                   } catch (e) {
                     items = [];
                   }
                   if (!Array.isArray(items)) items = [];

                   return (
                     <Table size="small">
                       <TableHead>
                         <TableRow>
                           <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{t('Products')}</TableCell>
                           <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{t('Qty')}</TableCell>
                           <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{t('Price')}</TableCell>
                           <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{t('Total')}</TableCell>
                         </TableRow>
                       </TableHead>
                       <TableBody>
                         {items.map((item, idx) => {
                           const pName = item.ProductName || item.productName || item.Name || item.name;
                           const qty = item.Quantity || item.quantity || 1;
                           const price = item.Price || item.price || 0;
                           return (
                             <TableRow key={idx}>
                               <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{pName}</TableCell>
                               <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{qty}</TableCell>
                               <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                                 {formatCurrency(price, selectedOrder.Currency || 'YER')}
                               </TableCell>
                               <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                                 {formatCurrency(price * qty, selectedOrder.Currency || 'YER')}
                               </TableCell>
                             </TableRow>
                           );
                         })}
                       </TableBody>
                     </Table>
                   );
                 })()}
               </Grid>
             </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrder} size={isMobile ? "small" : "medium"}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchSales;